import { Box, Tooltip } from "@mui/material";
import {
  DeleteOutlineOutlined as DeleteIcon,
  VisibilityOutlined as DetailIcon,
  VerifiedOutlined as VerifiedIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from "@mui/icons-material";
import { useState, type ReactNode } from "react";
import { macro } from "../ui/macroTokens";
import { MacroLine } from "../ui/MacroChips";
import { Labeled } from "../ui/primitives";
import { v2, nums, hoverReveal } from "../ui/v2Tokens";
import { servingLabel } from "../ui/format";
import useIsMobile from "../../../../hooks/useIsMobile";
import FoodDetailDialog from "../dialogs/FoodDetailDialog";
import { dominantMacroByGrams, itemNutrition, MACRO_LABELS, type FoodItemV2 } from "../model/nutritionV2Model";

const TAG_COLORS = {
  protein: { text: macro.protein.text, soft: macro.protein.soft },
  carbs: { text: macro.carbs.text, soft: macro.carbs.soft },
  fats: { text: macro.fats.text, soft: macro.fats.soft },
};

/**
 * Shared control chrome so quantity and serving line up pixel-for-pixel.
 *
 * `compact` is not just "a bit smaller": it is what tells an ALTERNATIVE apart from a food that
 * is actually in the meal. See the block comment on the row below.
 */
const controlSx = (compact?: boolean) => ({
  flexShrink: 0,
  boxSizing: "border-box" as const,
  height: controlHeight(compact),
  padding: "0 8px",
  borderRadius: v2.rSm,
  border: `1px solid ${v2.line}`,
  background: v2.surface,
  /**
   * 16px on a phone, whatever the row's density.
   *
   * iOS Safari zooms the page in on focus of any input under 16px and does not zoom back out. The
   * viewport meta used to hide that by banning zoom outright, which failed WCAG 1.4.4; now that
   * zoom is allowed again, the fields a trainer actually taps have to be big enough not to trigger
   * it. `compact` still reads smaller than a meal row — just not below the threshold.
   */
  fontSize: { xs: "16px", md: compact ? "12px" : "13px" },
  fontFamily: "inherit",
  color: compact ? v2.ink2 : v2.ink,
  outline: "none",
  ...nums,
  "&:focus": { borderColor: v2.brand },
});

/**
 * Taller on a phone, because these are tapped rather than clicked — and because at 16px the text
 * inside no longer fits a 27px box.
 *
 * ── why these are exported ──
 *
 * A responsive `sx` value is UNREADABLE from a rendered element in jsdom: MUI wraps even the `xs`
 * value in `@media (min-width:0px)`, and jsdom evaluates no media queries, so `toHaveStyle` sees
 * nothing at all. The compact-vs-normal difference is the entire signal that tells an alternative
 * apart from a food in the meal, so it cannot go untested — it is asserted against these objects
 * instead, at both widths. See FoodRowCore.compact.test.tsx.
 */
export const controlHeight = (compact?: boolean) =>
  compact ? { xs: "34px", md: "27px" } : { xs: "40px", md: "32px" };

/*
 * 56px fitted three characters. A quantity of 105, or 15.2 of a 33-gram unit, was cut off
 * mid-number — and a number you cannot read is one you cannot check. The native spinners were
 * taking a third of that width for arrows nobody uses on a field that steps by 0.5, so they go and
 * the digits get the room. A hair narrower on a phone, where the serving beside it needs the space
 * more and 16px digits are wide enough to read at 64.
 */
export const quantityWidth = (compact?: boolean) =>
  compact ? { xs: "58px", md: "60px" } : { xs: "64px", md: "76px" };

/**
 * Fixed on desktop, fluid on a phone.
 *
 * 150px cut "מנה 20.7 גרם (100 גרם)" off mid-parenthesis, and a serving you cannot read is one you
 * cannot check — so on desktop this is wide and FIXED, which is what lines the column up across
 * rows however the servings are named. On a phone there is no column to line up with: the control
 * is alone on its line and 200px would be two thirds of the screen, so it simply takes what is
 * left. The full label is still readable — the native picker shows it in full when tapped.
 */
export const servingWidth = (compact?: boolean) => ({
  width: { xs: "100%", md: compact ? "158px" : "200px" },
  minWidth: 0,
});

/** How a value reads when there is nothing to press: the control's text, without the box. */
const readOnlyValue = (compact?: boolean) => ({
  boxSizing: "border-box" as const,
  height: controlHeight(compact),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 8px",
  // read-only, so it is never focused and never triggers the zoom — it only has to MATCH the
  // editable version's size so a preview row and a real row are the same shape
  fontSize: { xs: "14px", md: compact ? "12px" : "13px" },
  color: v2.ink2,
  ...nums,
});

/** An icon control in the row's action cluster. Grows to a tappable 40px on a phone. */
export const rowIconBtn = {
  width: { xs: "40px", md: "30px" },
  height: { xs: "40px", md: "30px" },
  borderRadius: "6px",
  display: "grid",
  placeItems: "center",
  border: "none",
  background: "transparent",
  color: v2.ink3,
  cursor: "pointer",
  padding: 0,
  flexShrink: 0,
  transition: "background .12s, color .12s",
  "&:hover": { background: v2.lineSoft, color: v2.ink },
};

/**
 * The same icon control on a `compact` row — an alternative, or a substitution.
 *
 * Smaller than `rowIconBtn` at both widths, because `compact` is the signal that this row is
 * SECONDARY to the one above it and the controls carry part of that signal. It still clears a
 * thumb on a phone: 34px against the row's own much larger target.
 */
const compactIconSize = { xs: "34px", md: "26px" };

type Props = {
  item: FoodItemV2;
  /**
   * Omit any of these three and that control renders as TEXT instead — the same rule `onMove`
   * already follows. A row with nothing to press is how a preview states what it is; wiring a
   * trash and two inputs to no-op handlers instead, as the copy-from-a-meal dialog did, gives the
   * trainer three controls that answer nothing when pressed.
   */
  onQuantityChange?: (quantity: number) => void;
  onServingChange?: (servingSizeId: number) => void;
  onDelete?: () => void;
  compact?: boolean;
  /** omit to hide the reorder controls entirely (substitution groups don't need them) */
  onMove?: (direction: -1 | 1) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  /** an extra chip in the name line — the substitution count lives here */
  badge?: ReactNode;
  /**
   * Replaces the row's static name.
   *
   * A single-food alternative printed the food's name twice — once as the group's editable name
   * and once as the row's — so the group's name field moves INTO the row and the duplicate goes.
   */
  nameSlot?: ReactNode;
  /** Sits beside the macros: the alternative's delta against the food it replaces. */
  note?: ReactNode;
  /** extra icon controls beside delete, revealed with it on hover */
  actions?: ReactNode;
};

/**
 * Matches the meal header's ▲▼ chrome, one size down.
 *
 * 22×15 is a mouse target. On a phone the pair grows to 36×30 each — still stacked, so the two
 * together are 36×61 and a thumb can hit either without hitting the other.
 */
const moveBtn = (disabled: boolean) => ({
  width: { xs: "36px", md: "22px" },
  height: { xs: "30px", md: "15px" },
  borderRadius: "4px",
  border: "none",
  background: "transparent",
  color: disabled ? "#DCE2E8" : v2.ink3,
  cursor: disabled ? "default" : "pointer",
  display: "grid",
  placeItems: "center",
  padding: 0,
  fontFamily: "inherit",
  "&:hover": disabled ? {} : { background: v2.lineSoft, color: v2.ink },
});

/**
 * The row's shape ON A PHONE.
 *
 * The control cluster ALONE — a 76px quantity, the ×, a 200px serving and three 30px icons — comes
 * to ~390px, against a row that is ~330 wide at 375. So below `md` the line stops being a line:
 * what the food IS takes the first row, what you can CHANGE about it takes the second, and the
 * icons run down the end of both, where a thumb reaches them without covering the numbers it is
 * about to change.
 *
 * The reorder arrows keep their own leading column so they stay level with the name, and the icon
 * column spans both rows so it is one target cluster rather than two.
 *
 * ── desktop is untouched ──
 *
 * Above `md` the row keeps the exact grid and the exact DOM it always had: two or three columns,
 * with the quantity, the serving and the icons as ONE flex cluster in the last one. That cluster's
 * internal 6px gap and its `flex-end` alignment are not reproducible as grid columns without
 * changing what a desktop row looks like, which is why the structure is branched rather than
 * restyled. See the render below.
 *
 * None of this is `compact`. `compact` means "this row is an ALTERNATIVE, not a food in the meal"
 * (see the note above `controlSx`) and it means that at every width — a narrow desktop window must
 * never make a meal row read as an alternative.
 */
export const MOBILE_AREAS = {
  move: `"move name actions" "move controls actions"`,
  noMove: `"name actions" "controls actions"`,
};

export const COLUMNS = {
  move: { xs: "auto minmax(0, 1fr) auto", md: "auto minmax(0, 1fr) auto" },
  noMove: { xs: "minmax(0, 1fr) auto", md: "minmax(0, 1fr) auto" },
};

/** The editable food line — shared by meals, alternative meals and substitution groups. */
const FoodRowCore = ({
  item,
  onQuantityChange,
  onServingChange,
  onDelete,
  compact,
  onMove,
  canMoveUp,
  canMoveDown,
  badge,
  nameSlot,
  note,
  actions,
}: Props) => {
  // The row's one-word TAG, so it goes by grams. A tag sits beside the grams it describes and
  // loses any argument with them — "60 חלבון · 40 שומן" under a שומן label is a line nobody
  // believes, however true it is of the calories.
  const dom = dominantMacroByGrams(item.nutrition);
  const tag = TAG_COLORS[dom];
  const values = itemNutrition(item);
  const [detailOpen, setDetailOpen] = useState(false);
  /**
   * Structural: on a phone the icons leave the control cluster and become a column of their own.
   * A responsive `sx` cannot move an element to a different parent, and rendering both and hiding
   * one would put two "מחק בננה" buttons in the accessibility tree for every food in the menu.
   *
   * It also keeps the DESKTOP row byte-for-byte what it was — see the note on `MOBILE_AREAS`.
   */
  const isMobile = useIsMobile();

  return (
    <Box
      data-testid="food-row"
      data-compact={compact ? "true" : undefined}
      sx={{
        // a grid, not a flex row — the control column has to line up across rows
        // regardless of how long the food name or serving label is
        display: "grid",
        gridTemplateColumns: onMove ? COLUMNS.move : COLUMNS.noMove,
        /* The template and the `grid-area`s that reference it are declared TOGETHER, off the same
           flag, and never as a breakpoint pair. Splitting them is what broke the desktop row: the
           children kept naming areas the desktop template no longer defined, and an unmatched area
           name does not fall back to auto placement — it renames every implicit line, pushing those
           children out of the explicit grid entirely. Above md there is no template and no
           `grid-area`, so the columns fill in source order, exactly as they always did. */
        gridTemplateAreas: isMobile ? (onMove ? MOBILE_AREAS.move : MOBILE_AREAS.noMove) : undefined,
        alignItems: "center",
        gap: { xs: "8px 10px", md: "12px" },
        padding: compact
          ? { xs: "8px", md: "8px 10px" }
          : { xs: "10px 10px 12px", md: "12px 12px 12px 16px" },
        borderBottom: `1px solid ${v2.lineSoft}`,
        background: compact ? "transparent" : v2.surface,
        transition: "background .12s",
        "&:hover": { background: v2.hover },
        // the reorder arrows and the action icons are quiet until you reach for the row —
        // see `hoverReveal` for why this is opacity and not display
        ...hoverReveal.target,
      }}
    >
      {/* reorder controls first, matching where a meal card puts them */}
      {onMove && (
        <Box
          className={hoverReveal.className}
          sx={{
            /* Only where the template that defines it exists. A `grid-area` naming a line the grid
               does not have is NOT ignored: the spec says every implicit line then takes that name,
               so on desktop this placed the arrows in a column OUTSIDE the explicit grid and the
               controls fell into column 1 — which in RTL is the right edge. The whole row read
               back to front. */
            gridArea: isMobile ? "move" : undefined,
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            ...hoverReveal.item,
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label={`הזז למעלה ${item.name}`}
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
            sx={moveBtn(!canMoveUp)}
          >
            <UpIcon sx={{ fontSize: 13 }} />
          </Box>
          <Box
            component="button"
            type="button"
            aria-label={`הזז למטה ${item.name}`}
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
            sx={moveBtn(!canMoveDown)}
          >
            <DownIcon sx={{ fontSize: 13 }} />
          </Box>
        </Box>
      )}

      {/*
        The verification mark and the text block, as two columns.

        The mark took the position number's old column, and for the same reason the number lost
        it: nested inside THIS grid rather than the row's, it centres against the two text lines
        instead of against the taller controls beside them, so it sits level with the name and
        the macros it vouches for — and keeps doing so when a long name wraps.

        The column is reserved whether or not the food is checked. An icon that appears and
        disappears would shift every name in the meal by its width, and the empty cell is itself
        the answer to "was this one checked?" — nothing here means nobody verified it.
      */}
      <Box
        sx={{
          // mobile only — see the note on the reorder cell above
          gridArea: isMobile ? "name" : undefined,
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          gap: compact ? "8px" : "10px",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "grid", placeItems: "center", width: compact ? "16px" : "22px", flex: "none" }}>
          {/* The same provenance the trainee is shown in the app, so the trainer knows which
              of these figures were checked against a label and which are somebody's word.
              Fixed green, not the brand colour — the claim has to mean the same thing in
              every company's build. */}
          {item.verified && (
            <Tooltip title="ערכים שאומתו מול תווית המוצר" arrow placement="top">
              <VerifiedIcon sx={{ fontSize: compact ? 15 : 21, color: "#4CC38A" }} aria-label="מזון מאומת" />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: compact ? "6px" : "8px",
              flexWrap: "wrap",
              marginBottom: compact ? "1px" : "3px",
            }}
          >
            {nameSlot || (
              <Box
                sx={{
                  fontSize: compact ? "12.5px" : "14px",
                  fontWeight: 500,
                  color: compact ? v2.ink2 : v2.ink,
                }}
              >
                {item.name}
              </Box>
            )}
            {item.brand && <Box sx={{ fontSize: "11px", color: v2.ink4 }}>{item.brand}</Box>}
            {item.companyFoodId && (
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  padding: "1px 7px",
                  borderRadius: "5px",
                  whiteSpace: "nowrap",
                  background: "#F3F0FF",
                  color: "#5B4BA8",
                }}
              >
                של החברה
              </Box>
            )}
            <Box
              sx={{
                fontSize: compact ? "10px" : "11px",
                fontWeight: 500,
                padding: compact ? "0 6px" : "1px 7px",
                borderRadius: "5px",
                whiteSpace: "nowrap",
                background: item.isCustom ? v2.warnWash : tag.soft,
                color: item.isCustom ? v2.warn : tag.text,
              }}
            >
              {item.isCustom ? "חופשי" : MACRO_LABELS[dom]}
            </Box>
            {badge}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <MacroLine totals={values} size={compact ? 10.5 : 11.5} caloriesSize={compact ? 11 : 13} plain />
            {note}
          </Box>
        </Box>
      </Box>

      {/*
        quantity × serving, and the icons that act on the row.

        ── two structures, on purpose ──

        Above `md` these are what they have always been: ONE flex cluster in the row's last grid
        column, the icons bottom-aligned against the controls with a 6px gap. That gap and that
        alignment are properties of the cluster, not of the grid, and no arrangement of grid columns
        reproduces them exactly — so rather than approximate the desktop row, it is left alone.

        Below `md` they separate: the controls drop to a line of their own with the serving field
        taking the width, and the icons become a column spanning both lines at the row's end. Two
        things that move independently cannot be nested one inside the other, which is why this is a
        branch and not a media query.
      */}
      {isMobile ? (
        <>
          <Box
            data-testid="food-row-controls"
            sx={{
              gridArea: "controls",
              display: "flex",
              alignItems: "flex-end",
              gap: "6px",
              minWidth: 0,
              // on its own line it takes the whole line, so the serving field has something to fill
              width: "100%",
            }}
          >
            <Labeled label="כמות" hideLabel={compact} sx={{ flex: "0 0 auto" }}>
              {onQuantityChange ? (
              <Box
                component="input"
                type="number"
                aria-label={`כמות עבור ${item.name}`}
                min={0.1}
                step={0.5}
                value={item.quantity}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQuantityChange(Number(e.target.value))}
                sx={{
                  ...controlSx(compact),
                  /* 56px fitted three characters. A quantity of 105, or 15.2 of a 33-gram unit,
                     was cut off mid-number — and a number you cannot read is one you cannot check.
                     The native spinners were taking a third of that width for arrows nobody uses on
                     a field that steps by 0.5, so they go and the digits get the room. */
                  width: quantityWidth(compact),
                  textAlign: "center",
                  MozAppearance: "textfield",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
              ) : (
                <Box sx={{ ...readOnlyValue(compact), width: quantityWidth(compact), textAlign: "center" }}>
                  {item.quantity}
                </Box>
              )}
            </Labeled>
            <Box
              sx={{ color: v2.ink4, fontSize: compact ? "11px" : "12px", flexShrink: 0, paddingBottom: compact ? "5px" : "8px" }}
            >
              ×
            </Box>
            <Labeled
              label="יחידת מדידה"
              hideLabel={compact}
              // the field that takes whatever the row has left — on a phone that is the rest of the
              // line, on desktop it is nothing, because there the width is fixed on purpose
              sx={{ flex: { xs: "1 1 0", md: "0 0 auto" }, minWidth: 0 }}
            >
              {onServingChange ? (
              <Box
                component="select"
                aria-label={`יחידה עבור ${item.name}`}
                value={item.servingSizeId ?? ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onServingChange(Number(e.target.value))}
                disabled={item.isCustom}
                sx={{
                  ...controlSx(compact),
                  ...servingWidth(compact),
                  fontSize: { xs: "16px", md: compact ? "11.5px" : "12.5px" },
                  cursor: item.isCustom ? "not-allowed" : "pointer",
                  opacity: item.isCustom ? 0.6 : 1,
                }}
              >
                {item.servings.map((s) => (
                  <option key={s.id ?? s.servingName} value={s.id}>
                    {servingLabel(s)}
                  </option>
                ))}
              </Box>
              ) : (
                <Box
                  sx={{
                    ...readOnlyValue(compact),
                    ...servingWidth(compact),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {servingLabel(item.servings.find((s) => s.id === item.servingSizeId) || item.servings[0])}
                </Box>
              )}
            </Labeled>
          </Box>
          <Box
            data-testid="food-row-actions"
            sx={{
              gridArea: "actions",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              alignSelf: "center",
            }}
          >
            <Tooltip title="הצג ערכים תזונתיים מלאים" arrow placement="top">
              <Box
                component="button"
                type="button"
                aria-label={`ערכים תזונתיים של ${item.name}`}
                onClick={() => setDetailOpen(true)}
                sx={compact ? { ...rowIconBtn, width: compactIconSize, height: compactIconSize } : rowIconBtn}
              >
                <DetailIcon sx={{ fontSize: compact ? 15 : 17 }} />
              </Box>
            </Tooltip>
            {actions}
            {onDelete && (
            <Tooltip title="מחק" arrow placement="top">
              <Box
                component="button"
                type="button"
                aria-label={`מחק ${item.name}`}
                onClick={onDelete}
                sx={{
                  ...rowIconBtn,
                  ...(compact ? { width: compactIconSize, height: compactIconSize } : {}),
                  "&:hover": { background: v2.overWash, color: v2.over },
                }}
              >
                <DeleteIcon sx={{ fontSize: compact ? 15 : 16 }} />
              </Box>
            </Tooltip>
            )}
          </Box>
        </>
      ) : (
        <Box data-testid="food-row-controls" sx={{ display: "flex", alignItems: "flex-end", gap: "6px" }}>
            <Labeled label="כמות" hideLabel={compact} sx={{ flex: "0 0 auto" }}>
              {onQuantityChange ? (
              <Box
                component="input"
                type="number"
                aria-label={`כמות עבור ${item.name}`}
                min={0.1}
                step={0.5}
                value={item.quantity}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQuantityChange(Number(e.target.value))}
                sx={{
                  ...controlSx(compact),
                  /* 56px fitted three characters. A quantity of 105, or 15.2 of a 33-gram unit,
                     was cut off mid-number — and a number you cannot read is one you cannot check.
                     The native spinners were taking a third of that width for arrows nobody uses on
                     a field that steps by 0.5, so they go and the digits get the room. */
                  width: quantityWidth(compact),
                  textAlign: "center",
                  MozAppearance: "textfield",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
              ) : (
                <Box sx={{ ...readOnlyValue(compact), width: quantityWidth(compact), textAlign: "center" }}>
                  {item.quantity}
                </Box>
              )}
            </Labeled>
            <Box
              sx={{ color: v2.ink4, fontSize: compact ? "11px" : "12px", flexShrink: 0, paddingBottom: compact ? "5px" : "8px" }}
            >
              ×
            </Box>
            <Labeled
              label="יחידת מדידה"
              hideLabel={compact}
              // the field that takes whatever the row has left — on a phone that is the rest of the
              // line, on desktop it is nothing, because there the width is fixed on purpose
              sx={{ flex: { xs: "1 1 0", md: "0 0 auto" }, minWidth: 0 }}
            >
              {onServingChange ? (
              <Box
                component="select"
                aria-label={`יחידה עבור ${item.name}`}
                value={item.servingSizeId ?? ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onServingChange(Number(e.target.value))}
                disabled={item.isCustom}
                sx={{
                  ...controlSx(compact),
                  ...servingWidth(compact),
                  fontSize: { xs: "16px", md: compact ? "11.5px" : "12.5px" },
                  cursor: item.isCustom ? "not-allowed" : "pointer",
                  opacity: item.isCustom ? 0.6 : 1,
                }}
              >
                {item.servings.map((s) => (
                  <option key={s.id ?? s.servingName} value={s.id}>
                    {servingLabel(s)}
                  </option>
                ))}
              </Box>
              ) : (
                <Box
                  sx={{
                    ...readOnlyValue(compact),
                    ...servingWidth(compact),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {servingLabel(item.servings.find((s) => s.id === item.servingSizeId) || item.servings[0])}
                </Box>
              )}
            </Labeled>
          <Box data-testid="food-row-actions" sx={{ display: "flex", gap: "2px", marginBottom: "1px" }}>
            <Tooltip title="הצג ערכים תזונתיים מלאים" arrow placement="top">
              <Box
                component="button"
                type="button"
                aria-label={`ערכים תזונתיים של ${item.name}`}
                onClick={() => setDetailOpen(true)}
                sx={compact ? { ...rowIconBtn, width: compactIconSize, height: compactIconSize } : rowIconBtn}
              >
                <DetailIcon sx={{ fontSize: compact ? 15 : 17 }} />
              </Box>
            </Tooltip>
            {actions}
            {onDelete && (
            <Tooltip title="מחק" arrow placement="top">
              <Box
                component="button"
                type="button"
                aria-label={`מחק ${item.name}`}
                onClick={onDelete}
                sx={{
                  ...rowIconBtn,
                  ...(compact ? { width: compactIconSize, height: compactIconSize } : {}),
                  "&:hover": { background: v2.overWash, color: v2.over },
                }}
              >
                <DeleteIcon sx={{ fontSize: compact ? 15 : 16 }} />
              </Box>
            </Tooltip>
            )}
          </Box>
        </Box>
      )}

      <FoodDetailDialog item={item} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </Box>
  );
};

export default FoodRowCore;
